import {
  DashboardMetricCard,
  DashboardMetricGrid,
  DashboardSectionAction,
  DashboardSectionTitle,
  DashboardSectionSkeleton,
  PaneResponsiveCardGrid,
  PersonListCard,
  Button,
  formatClientDateTimeMedium,
  interviewerSessionHref,
  employerRecentApplicantToPersonModel,
  getHrefWithThreadPreservation,
  navigateWithThreadPreservation,
} from '@yeshuman/sdk'
import { CalendarClock, Users } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useInterviewerCandidates } from '../../hooks/use-interviewer-candidates'

export function InterviewerDashboardSections() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: candidates = [], isLoading } = useInterviewerCandidates()

  const unscheduled = candidates.filter((row) => !row.next_interview)
  const upcoming = candidates
    .filter((row) => row.next_interview)
    .slice()
    .sort((a, b) =>
      String(a.next_interview?.scheduled_start).localeCompare(
        String(b.next_interview?.scheduled_start),
      ),
    )
  const inProgress = candidates.filter((row) => row.live_session_id)

  return (
    <>
      <DashboardMetricGrid>
        <DashboardMetricCard
          title="To interview"
          value={isLoading ? '—' : String(unscheduled.length)}
          icon={Users}
        />
        <DashboardMetricCard
          title="Upcoming"
          value={isLoading ? '—' : String(upcoming.length)}
          icon={CalendarClock}
        />
      </DashboardMetricGrid>

      <div className="space-y-3">
        <DashboardSectionTitle id="interviewer-to-interview" title="To interview" />
        {isLoading ? (
          <DashboardSectionSkeleton cards={3} grid />
        ) : unscheduled.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Everyone in the interview stage already has a time booked.
          </p>
        ) : (
          <PaneResponsiveCardGrid>
            {unscheduled.slice(0, 6).map((row) => {
              const person = employerRecentApplicantToPersonModel(row)
              const href = getHrefWithThreadPreservation(
                interviewerSessionHref({
                  profileId: row.profile.id,
                  opportunityId: row.opportunity_id,
                  label: person.name,
                  liveSessionId: row.live_session_id,
                }),
              )
              return (
                <PersonListCard
                  key={row.id}
                  variant="compact"
                  person={person}
                  onOpen={() => navigate(href)}
                  openAriaLabel={`Interview ${person.name}`}
                />
              )
            })}
          </PaneResponsiveCardGrid>
        )}
      </div>

      <div className="space-y-3">
        <DashboardSectionTitle id="interviewer-upcoming" title="Upcoming" />
        {isLoading ? (
          <DashboardSectionSkeleton cards={2} grid />
        ) : upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">No interviews booked yet.</p>
        ) : (
          <PaneResponsiveCardGrid>
            {upcoming.slice(0, 6).map((row) => {
              const person = employerRecentApplicantToPersonModel(row)
              const href = getHrefWithThreadPreservation(
                interviewerSessionHref({
                  profileId: row.profile.id,
                  opportunityId: row.opportunity_id,
                  label: person.name,
                  interviewId: row.next_interview?.id,
                  liveSessionId: row.live_session_id,
                }),
              )
              return (
                <PersonListCard
                  key={row.id}
                  variant="compact"
                  person={person}
                  meta={
                    row.next_interview
                      ? formatClientDateTimeMedium(row.next_interview.scheduled_start)
                      : undefined
                  }
                  footer={
                    <Button type="button" size="sm" onClick={() => navigate(href)}>
                      Interview now
                    </Button>
                  }
                />
              )
            })}
          </PaneResponsiveCardGrid>
        )}
      </div>

      {inProgress.length > 0 ? (
        <p className="text-sm text-muted-foreground" role="status">
          {inProgress.length} live room{inProgress.length === 1 ? '' : 's'} in progress.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <DashboardSectionAction
          onClick={() =>
            navigateWithThreadPreservation(navigate, '/interviewer/candidates', searchParams)
          }
        >
          Candidates
        </DashboardSectionAction>
        <DashboardSectionAction
          onClick={() =>
            navigateWithThreadPreservation(navigate, '/interviewer/interviews', searchParams)
          }
        >
          Interviews
        </DashboardSectionAction>
      </div>
    </>
  )
}
